import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Send, Loader2, Clock, Mail, Calendar, CheckCircle2, XCircle, BarChart3 } from 'lucide-react';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch (_) {
    return iso;
  }
};

const WeeklyReportSettings = () => {
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [trend, setTrend] = useState([]);
  const [loadingTrend, setLoadingTrend] = useState(true);

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get('/admin/reports/weekly/history');
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history || []);
      }
    } catch (e) {
      // silent
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchTrend = async () => {
    setLoadingTrend(true);
    try {
      const res = await api.get('/admin/reports/analytics/weekly-trend?weeks=8');
      if (res.ok) {
        const data = await res.json();
        setTrend(data.weeks || []);
      }
    } catch (e) {
      // silent
    } finally {
      setLoadingTrend(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    fetchTrend();
  }, []);

  const handleSendNow = async () => {
    setSending(true);
    try {
      const res = await api.post('/admin/reports/weekly/send', {});
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Rapport envoyé');
        fetchHistory();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Échec de l\'envoi');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="p-6" data-testid="weekly-report-settings">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[#4C8F6E]/10 flex items-center justify-center">
          <Mail className="h-5 w-5 text-[#4C8F6E]" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-[#0F2744]">Rapports hebdomadaires</h2>
          <p className="text-sm text-gray-500">Récapitulatif automatique envoyé aux administrateurs</p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-5 flex items-start gap-3">
        <Clock className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <strong>Envoi automatique :</strong> chaque lundi à 08h00 (UTC).<br />
          Contenu : nouvelles candidatures (top 5 par score), nouveaux leads, évaluations complétées, décisions clients et candidats rejetés/restaurés sur les 7 derniers jours.
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 p-4 rounded-lg border border-gray-200">
        <div>
          <p className="font-medium text-gray-800">Envoyer le rapport maintenant</p>
          <p className="text-xs text-gray-500">Déclenche un envoi manuel (récap des 7 derniers jours).</p>
        </div>
        <Button
          onClick={handleSendNow}
          disabled={sending}
          className="bg-[#4C8F6E] hover:bg-[#3d7a5c] text-white"
          data-testid="send-weekly-report-btn"
        >
          {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
          {sending ? 'Envoi...' : 'Envoyer le rapport'}
        </Button>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="h-4 w-4 text-gray-500" />
          <h3 className="font-medium text-gray-800">Évolution sur 8 semaines</h3>
        </div>
        {loadingTrend ? (
          <div className="text-center py-6 text-sm text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : trend.length === 0 ? (
          <p className="text-sm text-gray-400 italic text-center py-4">Pas assez de données</p>
        ) : (
          <div className="mb-6" data-testid="weekly-trend-chart">
            {(() => {
              const maxVal = Math.max(1, ...trend.flatMap(w => [w.new_candidates, w.evaluations, w.hired]));
              return (
                <>
                  <div className="flex items-end justify-between gap-2 h-36 px-2 pt-2 border-b border-gray-200 bg-gradient-to-b from-gray-50/50 to-transparent rounded-t-md">
                    {trend.map((w, i) => {
                      const candH = (w.new_candidates / maxVal) * 100;
                      const evalH = (w.evaluations / maxVal) * 100;
                      const hireH = (w.hired / maxVal) * 100;
                      return (
                        <div key={w.week_start} className="flex-1 flex flex-col items-center gap-0.5 group relative">
                          <div className="flex items-end gap-1 h-full w-full justify-center">
                            <div
                              className="w-3 bg-[#0F2744] rounded-t-sm transition-all hover:opacity-80"
                              style={{ height: `${candH}%`, minHeight: w.new_candidates > 0 ? '4px' : '0' }}
                              title={`Candidats: ${w.new_candidates}`}
                            />
                            <div
                              className="w-3 bg-[#4C8F6E] rounded-t-sm transition-all hover:opacity-80"
                              style={{ height: `${evalH}%`, minHeight: w.evaluations > 0 ? '4px' : '0' }}
                              title={`Évaluations: ${w.evaluations}`}
                            />
                            <div
                              className="w-3 bg-green-600 rounded-t-sm transition-all hover:opacity-80"
                              style={{ height: `${hireH}%`, minHeight: w.hired > 0 ? '4px' : '0' }}
                              title={`Embauchés: ${w.hired}`}
                            />
                          </div>
                          {/* Tooltip on hover */}
                          <div className="absolute bottom-full mb-1 bg-gray-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                            <div>{w.week_start}</div>
                            <div>{w.new_candidates} cand · {w.evaluations} éval · {w.hired} emb</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between text-[10px] text-gray-400 px-2 mt-1">
                    {trend.map((w) => (
                      <span key={`${w.week_start}-label`} className="flex-1 text-center">{w.week_label}</span>
                    ))}
                  </div>
                  <div className="flex items-center justify-center gap-4 mt-3 text-xs">
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#0F2744] rounded-sm" /> Candidats</span>
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#4C8F6E] rounded-sm" /> Évaluations</span>
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-green-600 rounded-sm" /> Embauchés</span>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="h-4 w-4 text-gray-500" />
          <h3 className="font-medium text-gray-800">Historique des envois</h3>
        </div>

        {loadingHistory ? (
          <div className="text-center py-6 text-sm text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
            Chargement...
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg" data-testid="no-reports-yet">
            Aucun rapport envoyé pour le moment.
          </div>
        ) : (
          <div className="space-y-2" data-testid="reports-history">
            {history.map((h, i) => {
              const okCount = (h.sent_ok || []).length;
              const failCount = (h.sent_failed || []).length;
              const allOk = failCount === 0 && okCount > 0;
              return (
                <div key={h.sent_at || i} className="flex items-start gap-3 p-3 border border-gray-100 rounded-lg bg-gray-50">
                  {allOk ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium text-gray-800">{fmtDate(h.sent_at)}</span>
                      {h.manual && (
                        <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full uppercase font-semibold">Manuel</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      Envoyé à {okCount} destinataire{okCount > 1 ? 's' : ''}
                      {failCount > 0 && <span className="text-red-500"> — {failCount} échec{failCount > 1 ? 's' : ''}</span>}
                      {h.summary && (
                        <> · {h.summary.candidates ?? 0} candidat{(h.summary.candidates ?? 0) > 1 ? 's' : ''} · {h.summary.leads ?? 0} lead{(h.summary.leads ?? 0) > 1 ? 's' : ''} · {h.summary.evaluations ?? 0} éval.</>
                      )}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
};

export default WeeklyReportSettings;
